import { AxiosPromise } from 'axios';
import { useState } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const makeApiRequest = async <T = any>(
  httpPromise: AxiosPromise<T>,
): Promise<ApiResponse<T>> => {
  try {
    const response = await httpPromise;
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    const message =
      error.response.data.message ||
      error.response.data.Message ||
      'Something went wrong, please try again!';
    return {
      success: false,
      error: {
        isAxiosError: false,
        response: {
          status: error.response.status,
          data: {
            message,
          },
        },
        name: error.response.statusText,
        message,
      },
    };
  }
};

export type ApiResponse<T> =
  | {
      success: true;
      data?: T;
    }
  | {
      success: false;
      error?: ErrorResponse;
    };

interface ErrorResponse extends Error {
  isAxiosError: boolean;
  response: {
    status: number;
    data?: {
      message?: string;
    };
  };
}

interface ErrorProps {
  status: number;
  message: string;
  isAxiosError?: boolean;
  name?: string;
}

export const setErrorResponse = ({
  status,
  message,
  isAxiosError = true,
  name = 'Custom Created Error',
}: ErrorProps): ErrorResponse => {
  return {
    name,
    message,
    isAxiosError,
    response: {
      status,
      data: {
        message,
      },
    },
  };
};

export interface RequestStateSuccess<T, U = null> {
  type: 'REQUEST_SUCCESS';
  data: T;
  extra?: U;
}

export const isSucceeded = <T>(state: RequestState<T>): boolean =>
  state.type === 'REQUEST_SUCCESS';
export const isLoading = <T>(state: RequestState<T>): boolean =>
  state.type === 'REQUEST_START';
export const isFailed = <T>(state: RequestState<T>): boolean =>
  state.type === 'REQUEST_ERROR';

export const requestInit = () =>
  ({ type: 'REQUEST_INIT', data: null } as const);
export const requestStart = () =>
  ({ type: 'REQUEST_START', data: null } as const);
export const requestSuccess = <T, U = null>(
  data: T,
  extra?: U,
): RequestStateSuccess<T, U> =>
  ({ type: 'REQUEST_SUCCESS', data, extra } as const);

export const requestError = (error: ErrorResponse) =>
  ({ type: 'REQUEST_ERROR', error, data: null } as const);

export type RequestState<T> =
  | ReturnType<typeof requestInit | typeof requestStart | typeof requestError>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | RequestStateSuccess<T, any>;

export const useRequestState = <T>(currentState?: RequestState<T>) => {
  const [requestState, setRequestState] = useState<RequestState<T>>(
    currentState || requestInit(),
  );
  return [requestState, setRequestState] as const;
};
